import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { catchError, EMPTY, from, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ENVIRONMENT } from '../model/common-environment';
import { UsersPermissionsUserRegistration } from '../model/valibot/users-permissions-user-registration.schema';
import { UsersPermissionsUser } from '../model/valibot/users-permissions-user.schema';
import {
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from './storage.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly httpClient = inject(HttpClient);
  private readonly navController = inject(NavController);
  private readonly toastService = inject(ToastService);
  private readonly environment = inject(ENVIRONMENT);
  private readonly authUrl = `${this.environment.apiRestEndpoint}/auth/local`;
  private readonly _loggedInStatus = signal<boolean>(false);
  public readonly loggedInStatus = this._loggedInStatus.asReadonly();
  private readonly _user = signal(this.getPersistedUser());
  public readonly user = this._user.asReadonly();

  public constructor() {
    this._loggedInStatus.set(this.getLoggedIn());
  }

  public login(
    identifier: string,
    password: string,
  ): Observable<UsersPermissionsUserRegistration> {
    return this.httpClient
      .post<UsersPermissionsUserRegistration>(this.authUrl, {
        identifier,
        password,
      })
      .pipe(
        tap(
          (
            usersPermissionsUserRegistration: UsersPermissionsUserRegistration,
          ) => {
            this.persistUserFromAuthResponse(usersPermissionsUserRegistration);
          },
        ),
      );
  }

  public register(
    username: string,
    email: string,
    password: string,
  ): Observable<UsersPermissionsUserRegistration> {
    return this.httpClient
      .post<UsersPermissionsUserRegistration>(`${this.authUrl}/register`, {
        username,
        email,
        password,
      })
      .pipe(
        tap((user: UsersPermissionsUserRegistration) => {
          this.persistUserFromAuthResponse(user);
        }),
      );
  }

  public logout(): void {
    void this.navController.navigateRoot('/').finally(() => {
      this._loggedInStatus.set(false);
      this.resetUser();
    });
  }

  public deleteAccount(): void {
    this.httpClient
      .delete(`${this.environment.apiRestEndpoint}/user/destroyMe`, {
        headers: this.getAuthHeader(),
      })
      .pipe(
        tap(() => void this.navController.navigateRoot('/')),
        tap(() => {
          this._loggedInStatus.set(false);
        }),
        tap(() => {
          this.resetUser();
        }),
        tap(() => void this.toastService.successDeletedToast()),
      )
      .subscribe();
  }

  public getAuthHeader(): { Authorization: string } {
    return { Authorization: `Bearer ${this.getPersistedToken()}` };
  }

  public forgotPassword(email: string): Observable<void> {
    return this.httpClient
      .post(`${this.environment.apiRestEndpoint}/auth/forgot-password`, {
        email,
      })
      .pipe(
        catchError((error) => {
          console.log('An error occurred:', error.response);
          return EMPTY;
        }),
        map(() => void 0),
      );
  }

  public resetPassword(
    password: string,
    passwordConfirmation: string,
    code: string,
  ): void {
    this.httpClient
      .post<UsersPermissionsUserRegistration>(
        `${this.environment.apiRestEndpoint}/auth/reset-password`,
        { code, password, passwordConfirmation },
      )
      .pipe(
        tap((user: UsersPermissionsUserRegistration) => {
          this.persistUserFromAuthResponse(user);
        }),
        switchMap(() => from(this.navController.navigateRoot('/'))),
        switchMap(() => from(this.toastService.successLoggedIn())),
        catchError((error) => {
          console.log('An error occurred:', error.response);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public facebookLogin(): void {
    window.location.href = `${this.environment.apiRestEndpoint}/connect/facebook`;
  }

  public googleLogin(): void {
    window.location.href = `${this.environment.apiRestEndpoint}/connect/google`;
  }

  public checkProviderAuth(route: ActivatedRouteSnapshot): Observable<boolean> {
    const accessToken = route.queryParams['access_token'];
    const provider = route.paramMap.get('provider');
    if (accessToken && provider) {
      return this.httpClient
        .get<UsersPermissionsUserRegistration>(
          `${this.environment.apiRestEndpoint}/auth/${provider}/callback?access_token=${accessToken}`,
        )
        .pipe(
          tap(() => void this.toastService.successLoggedIn()),
          tap((user: UsersPermissionsUserRegistration) => {
            this.persistUserFromAuthResponse(user);
          }),
          tap(() => void this.navController.navigateRoot('/')),
          map(() => true),
          catchError((error) => {
            console.log('An error occurred:', error.response);
            return EMPTY;
          }),
        );
    }
    return of(true);
  }

  private persistUserFromAuthResponse({
    jwt,
    user,
  }: UsersPermissionsUserRegistration): void {
    if (user?.id && user.email && user.username && jwt) {
      localStorageSetItem('userId', user.id.toString());
      localStorageSetItem('userEmail', user.email);
      localStorageSetItem('username', user.username);
      localStorageSetItem('createdAt', user.createdAt ?? '');
      localStorageSetItem('loggedIn', 'true');
      localStorageSetItem('marvin', jwt);
      this._loggedInStatus.set(this.getLoggedIn());
      this._user.set(this.getPersistedUser());
    }
  }

  private resetUser(): void {
    ['userId', 'userEmail', 'username', 'loggedIn', 'token'].forEach((item) => {
      localStorageRemoveItem(item);
    });
    this._user.set(null);
  }

  private getPersistedUser(): UsersPermissionsUser | null {
    const id = localStorageGetItem('userId') || null;
    const username = localStorageGetItem('username') || null;
    const email = localStorageGetItem('userEmail') || null;
    if (id && username && email) {
      return {
        id: Number(id),
        username,
        email,
        photoURL: undefined,
        createdAt: localStorageGetItem('createdAt') || undefined,
      };
    }
    return null;
  }

  private getLoggedIn(): boolean {
    return localStorageGetItem('loggedIn') === 'true';
  }

  private getPersistedToken(): string {
    return localStorageGetItem('marvin') || '';
  }
}
